import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { getConsignmentUrl } from '~/entities/consignment'
import { usePayors } from '~/entities/payors'
import { USIndicator } from '~/entities/reviewer'
import { useUserStore } from '~/entities/user'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { Button, DataTable, LocalIcon, Text, Tooltip } from '~/shared/ui'
import { TABLE_COLUMNS, TABLE_COLUMNS_MAP } from '../config'
import { useFilters, usePaginatedConsignments, useTableSettings } from '../model'
import { StatusChip } from './status-chip'

const columnHelper = createColumnHelper<apiClient.Consignment>()

const getColumns = ({
  rowDisabledFn,
  handleSortFn,
  sortBy,
  sortOrder,
  payors,
  hiddenColumns,
}: {
  rowDisabledFn: (row: apiClient.Consignment) => boolean
  handleSortFn: (column: keyof typeof TABLE_COLUMNS_MAP) => void
  sortBy: keyof typeof TABLE_COLUMNS_MAP
  sortOrder: string
  payors: apiClient.Payor[]
  hiddenColumns: (keyof typeof TABLE_COLUMNS_MAP)[]
}) => [
  ...(hiddenColumns.includes(TABLE_COLUMNS.KEY)
    ? []
    : [
        columnHelper.accessor('key', {
          header: () => (
            <div className="-ml-3 flex gap-2.5">
              <USIndicator className="invisible h-4" />
              {TABLE_COLUMNS_MAP[TABLE_COLUMNS.KEY].label}
            </div>
          ),
          cell: (info) => (
            <div className="-ml-3 flex items-center gap-2.5">
              <USIndicator
                className={cn({ invisible: !info.row.original.onshore_only })}
                align="start"
              />
              <div className="flex h-9 flex-col justify-center whitespace-nowrap">
                <Text
                  className="uppercase"
                  appearance="BODY2"
                >
                  {info.getValue()}
                </Text>
              </div>
            </div>
          ),
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.DATE_RECEIVED)
    ? []
    : [
        columnHelper.accessor('inbox_info.date', {
          header: () => {
            return (
              <div className="flex items-center">
                {TABLE_COLUMNS_MAP[TABLE_COLUMNS.DATE_RECEIVED].label}
                <button
                  className="cursor-pointer border-none bg-transparent px-1"
                  type="button"
                  onClick={() => handleSortFn(TABLE_COLUMNS.DATE_RECEIVED)}
                >
                  <LocalIcon
                    className={cn('ml-2 h-4.5 w-4.5', {
                      'text-content-high-emphasis fill-content-medium-emphasis':
                        sortBy === TABLE_COLUMNS.DATE_RECEIVED,
                      'text-content-medium-emphasis fill-content-high-emphasis':
                        sortBy === TABLE_COLUMNS.DATE_RECEIVED && sortOrder === 'desc',
                    })}
                    name="sort"
                  />
                </button>
              </div>
            )
          },
          cell: (info) => {
            const value = info.getValue()
            if (!value) return ''
            return <Text appearance="SUBTITLE2">{formatDateDefault(value)}</Text>
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.SOURCE)
    ? []
    : [
        columnHelper.accessor('inbox_info', {
          header: TABLE_COLUMNS_MAP[TABLE_COLUMNS.SOURCE].label,
          cell: (info) => {
            const inbox = info.getValue()
            if (!inbox) return ''
            // Show name and type as example
            return (
              <div className="flex flex-col">
                <Text appearance="SUBTITLE2">{`${inbox.name ?? ''}`}</Text>
                <Text appearance="CAPTION">
                  <span
                    className={cn('text-text-secondary', {
                      'text-text-disabled': rowDisabledFn(info.row.original),
                    })}
                  >{`(${inbox.region ?? ''})`}</span>
                </Text>
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.PAYOR)
    ? []
    : [
        columnHelper.accessor('payor_id', {
          header: TABLE_COLUMNS_MAP[TABLE_COLUMNS.PAYOR].label,
          cell: (info) => {
            const payorId = info.getValue()
            if (!payorId) return ''
            const payor = payors.find((p) => p.payorId === payorId)
            return (
              <div className="flex flex-col">
                <Tooltip content={payor?.payorName}>
                  <Text
                    className="w-fit"
                    appearance="SUBTITLE2"
                  >
                    {payorId}
                  </Text>
                </Tooltip>
                {payor?.payorName && (
                  <Text appearance="CAPTION">
                    <span
                      className={cn('text-text-secondary', {
                        'text-text-disabled': rowDisabledFn(info.row.original),
                      })}
                    >
                      {payor?.payorName}
                    </span>
                  </Text>
                )}
              </div>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.REVIEWER)
    ? []
    : [
        columnHelper.accessor('reviewer', {
          header: TABLE_COLUMNS_MAP[TABLE_COLUMNS.REVIEWER].label,
          cell: (info) => {
            const reviewer = info.getValue()
            if (!reviewer) {
              return (
                <Text
                  className="text-text-secondary"
                  appearance="BODY2"
                >
                  Awaiting reviewer
                </Text>
              )
            }
            return (
              <Text appearance="BODY2">
                {reviewer.first_name} {reviewer.last_name}
              </Text>
            )
          },
        }),
      ]),
  ...(hiddenColumns.includes(TABLE_COLUMNS.STATUS)
    ? []
    : [
        columnHelper.accessor('review_status', {
          header: TABLE_COLUMNS_MAP[TABLE_COLUMNS.STATUS].label,
          cell: (info) => {
            const status = info.getValue()
            if (!status) return ''
            return (
              <StatusChip
                status={status as apiClient.ReviewStatusEnum}
                hasReviewer={!!info.row.getValue('reviewer')}
              />
            )
          },
        }),
      ]),
]

export function ConsignmentsTable() {
  const { data } = usePaginatedConsignments()
  const { filters, setFilters, resetFilters } = useFilters()
  const userInfo = useUserStore((state) => state.userInfo)
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const { hiddenColumns } = useTableSettings()

  const rowDisabledFn = useCallback(
    (row: apiClient.Consignment) => {
      return (
        row.review_status === 'pending' && !!row.reviewer && row.reviewer.sub !== userInfo?.email
      )
    },
    [userInfo?.email],
  )

  const handleSortFn = useCallback(
    (column: keyof typeof TABLE_COLUMNS_MAP) => {
      if (filters.sortBy === column) {
        setFilters({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })
      } else {
        setFilters({ sortBy: column, sortOrder: 'asc' })
      }
    },
    [filters.sortBy, filters.sortOrder, setFilters],
  )

  const columns = useMemo(
    () =>
      getColumns({
        rowDisabledFn,
        handleSortFn,
        sortBy: filters.sortBy as keyof typeof TABLE_COLUMNS_MAP,
        sortOrder: filters.sortOrder,
        payors,
        hiddenColumns,
      }),
    [filters.sortBy, filters.sortOrder, payors, hiddenColumns, rowDisabledFn, handleSortFn],
  )

  return (
    <DataTable
      columns={columns}
      data={data.items}
      containerClassName="[&_>_div]:flex [&_>_div]:h-full [&_>_div]:flex-col [&_>_div]:justify-between"
      withStickyHeader
      getRowUrl={(row: apiClient.Consignment) => getConsignmentUrl(row.key)}
      noResultsMessage="Looks like your filters are a bit too specific – or maybe the docs just haven't arrived."
      noResultsAction={<Button onClick={resetFilters}>Clear filters</Button>}
      disabledRowTip={(row: apiClient.Consignment) => {
        if (rowDisabledFn(row)) {
          return `${row.reviewer?.first_name} is working on this consignment and it is currently locked.`
        }
        return null
      }}
    />
  )
}
